import { useContext, useEffect, useRef, useState } from "react";
import { AccountContext } from "../contexts/AccountContexts";
import { loadingActive } from "../components/Background";
import { ContentContext } from "../contexts/ContentContext";
import { useConfirm } from "../contexts/ModalContexts";
import { useTranslation } from "react-i18next";
import { requestSetDefaultPriorityLevel } from "../requests/setup_requests";
import { darkAndLightThemeDefault } from "../components/ThemeComponents";
import { PermissionContext } from "../contexts/PermissionContexts";

export const StyleSelectScreen = ({ styles, target = "H", props, getData }) => {
  const [openSelect, setOpenSelect] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(
    props.style ? props.style : styles[0],
  );
  const [placeholder, setPlaceholder] = useState("");
  const selectRef = useRef(null);

  const handleSelectStyle = (style) => {
    setSelectedStyle(style);
  };

  useEffect(() => {
    if (selectedStyle !== "" || selectedStyle !== null) {
      getData(selectedStyle);
    }
  }, [selectedStyle]);

  useEffect(() => {
    if (target !== null && target.trim() !== "") {
      setPlaceholder(target);
    } else if (target.trim() == "") {
      setPlaceholder("H");
    }
  }, [target]);

  const handleOpenSelect = () => {
    setOpenSelect((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpenSelect(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="form-control relative">
      <div className="h-10 pl-[6px] w-52">
        <div
          className={`cursor-pointer select-none px-[6px] min-w-8 p-1 max-w-max text-center truncate text-nowrap rounded-full  outline outline-2 outline-white dark:outline-gray-700  ${selectedStyle}`}
          onClick={handleOpenSelect}
        >
          <span>{placeholder}</span>
        </div>
      </div>

      {openSelect && (
        <div
          className="flex flex-wrap justify-center min-w-48 max-w-max gap-2 absolute top-full ltr:-left-4 rtl:-right-4 bg-white dark:bg-gray-700 dark:border-gray-500  rounded-lg border border-gray-400 p-2"
          ref={selectRef}
        >
          {styles.map((style, index) => (
            <div
              key={index}
              className={`cursor-pointer select-none w-8 p-1 rounded-full flex items-center justify-center outline outline-2 outline-white dark:outline-gray-700 ${selectedStyle === style ? "ring-[4px] ring-sky-600" : ""} ${style}`}
              onClick={() => {
                handleSelectStyle(style);
              }}
            >
              <span>H</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SetupElementEdit = ({
  props,
  setOpen,
  setElement,
  edit,
  hasDefault = false,
  isDeletable = true,
  styles,
  requestNewElement,
  requestEditElement,
  requestDeleteElement,
}) => {
  const { t } = useTranslation("settings");
  const { user } = useContext(AccountContext);
  const { hasPermission } = useContext(PermissionContext);
  const { openErrorModal } = useContext(ContentContext);
  const confirm = useConfirm();
  const [title, setTitle] = useState(props.title ? props.title : "");
  const [value, setValue] = useState(props.value ? props.value : 0);
  const [style, setStyle] = useState(props.style ? props.style : "");
  const [isDefault, setIsDefault] = useState(
    props.default ? props.default : false,
  );

  const handleSubmit = () => {
    loadingActive(true);
    if (!edit) {
      requestNewElement({
        userId: user.id,
        title: title,
        value: value,
        style: style,
      })
        .then((data) => {
          setElement(data);
          setOpen(false);
          loadingActive(false);
        })
        .catch((error) => {
          openErrorModal(error);
          loadingActive(false);
        });
    } else {
      if (hasDefault && !props.default && isDefault) {
        requestSetDefaultPriorityLevel({ id: props.id })
          .then((data) => {
            setElement(data);
          })
          .catch((error) => {
            openErrorModal(error);
          });
      }
      requestEditElement({
        userId: user.id,
        id: props.id,
        title: title,
        value: value,
        style: style,
      })
        .then((data) => {
          setElement(data);
          setOpen(false);
          loadingActive(false);
        })
        .catch((error) => {
          openErrorModal(error);
          loadingActive(false);
        });
    }
  };

  const toggleDefault = () => setIsDefault(!isDefault);

  const handleDelete = async () => {
    const isConfirmed = await confirm(
      t(
        "Are you sure you want to delete this item? This action cannot be undone.",
      ),
    );
    if (isConfirmed) {
      loadingActive(true);
      requestDeleteElement({ userId: user.id, id: props.id })
        .then((data) => {
          setOpen((prev) => false);
          setElement(data);
          loadingActive(false);
        })
        .catch((error) => {
          openErrorModal(error);
          loadingActive(false);
        });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div className="grid grid-cols-5 w-full gap-3 items-center">
      <label className="form-control">
        <input
          type="text"
          placeholder={t("Title")}
          className="input input-bordered input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
          required={true}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
        />
      </label>
      <label className="form-control">
        <input
          type="number"
          data-theme="light"
          placeholder={t("Value")}
          className="input input-bordered  input-md w-full h-10 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-800 dark:text-gray-300"
          required={true}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
          }}
        />
      </label>

      {hasDefault && hasPermission("priority:set_default") && (
        <div className="form-control">
          <label className="label cursor-pointer gap-2 justify-center">
            <span className="label-text">{t("Set as default")}</span>
            {/* <input type="checkbox" className="toggle checked:bg-green-600" checked={isDefault} onChange={toggleDefault} /> */}
            <input
              type="radio"
              name="radio-1"
              className="radio checked:bg-green-600"
              checked={isDefault}
              onChange={toggleDefault}
            />
          </label>
        </div>
      )}

      <StyleSelectScreen
        styles={styles}
        target={title}
        props={props}
        getData={setStyle}
      />
      <div className="flex gap-3 mt-auto ltr:ml-auto rtl:mr-auto">
        <button
          className="btn bg-green-600 hover:bg-green-500 border-none text-white"
          onClick={handleSubmit}
        >
          {t("Submit")}
        </button>
        {edit && (
          <button
            className="btn bg-red-600 hover:bg-red-500 border-none text-white"
            disabled={(hasDefault && props.default) || !isDeletable}
            onClick={handleDelete}
          >
            {t("Delete")}
          </button>
        )}
        <button
          className={`btn ${darkAndLightThemeDefault}`}
          onClick={handleClose}
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
};
